import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Lock, CheckCircle, AlertTriangle } from 'lucide-react';
import Navbar from '../../components/Navbar';

const PasswordResetConfirm = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    password: '',
    passwordConfirm: ''
  });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Validate token on mount
    if (!token) {
      setTokenValid(false);
      setValidating(false);
      setError('Kein Token gefunden. Bitte fordere einen neuen Link an.');
      return;
    }
    
    // Token exists, consider it valid for now (backend will validate on submit)
    setTokenValid(true);
    setValidating(false);
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    if (!/[a-zA-Z]/.test(formData.password)) {
      setError('Das Passwort muss mindestens einen Buchstaben enthalten');
      return;
    }

    if (!/[0-9]/.test(formData.password)) {
      setError('Das Passwort muss mindestens eine Zahl enthalten');
      return;
    }

    if (formData.password !== formData.passwordConfirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          new_password: formData.password
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.detail || 'Dieser Link ist ungültig oder abgelaufen.');
        setTokenValid(false);
      }
    } catch (error) {
      console.error('Password reset confirm error:', error);
      setError('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br bg-[#F4F2F2]">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-md mx-auto">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 text-gray-600 hover:text-[#2B2142] mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            Zurück zum Login
          </button>

          <div className="bg-white rounded-3xl shadow-xl p-8">
            {validating ? (
              <div className="text-center py-8">
                <div className="animate-spin w-12 h-12 border-4 border-[#2B2142] border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600">Token wird überprüft...</p>
              </div>
            ) : success ? (
              <div className="text-center" data-testid="reset-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Passwort erfolgreich geändert!</h2>
                <p className="text-gray-600 mb-8">
                  Dein Passwort wurde erfolgreich aktualisiert. Du kannst dich jetzt mit deinem neuen Passwort anmelden.
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="px-6 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl"
                  data-testid="go-to-login"
                >
                  Zum Login
                </button>
              </div>
            ) : !tokenValid ? (
              <div className="text-center" data-testid="token-invalid">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Link ungültig oder abgelaufen</h2>
                <p className="text-gray-600 mb-8">
                  {error || 'Dieser Link ist ungültig oder abgelaufen. Bitte fordere einen neuen Link zum Zurücksetzen deines Passworts an.'}
                </p>
                <button
                  onClick={() => navigate('/password-reset')}
                  className="px-6 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl"
                  data-testid="request-new-link"
                >
                  Neuen Link anfordern
                </button>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-[#FFF0D1] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-[#2B2142]" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Neues Passwort setzen</h1>
                  <p className="text-gray-600">Wähle ein sicheres neues Passwort für dein EventConnect-Konto.</p>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl" data-testid="error-message">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Neues Passwort
                    </label>
                    <input
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                      placeholder="Mind. 8 Zeichen, Buchstaben + Zahlen"
                      data-testid="new-password-input"
                      minLength={8}
                    />
                    <p className="text-xs text-gray-500 mt-1">Mindestens 8 Zeichen mit Buchstaben und Zahlen</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Passwort bestätigen
                    </label>
                    <input
                      type="password"
                      required
                      value={formData.passwordConfirm}
                      onChange={(e) => setFormData({ ...formData, passwordConfirm: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                      placeholder="Passwort wiederholen"
                      data-testid="confirm-password-input"
                      minLength={8}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl disabled:opacity-50 transition-colors"
                    data-testid="reset-confirm-btn"
                  >
                    {loading ? 'Wird gespeichert...' : 'Passwort speichern'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetConfirm;
